import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import styled from "styled-components";
import Button from "@/src/components/atom/Button/BasicButton";
import Icon from "@/src/components/atom/Icon";
import Typo from "@/src/components/atom/Typo";
import MobileMenuPanel from "@/src/components/organism/MobileMenuPanel";
import colorSet from "@/src/styles/color";
import logoExport from "@/src/assets/logos/logo-export.svg";
import logoExportDev from "@/src/assets/logos/logo-export-dev.svg";
import GlobalLanguageSvg from "@/src/assets/icons/icon-global-language.svg";
import MenuIconSvg from "@/src/assets/icons/icon-menu.svg";
import { isDevelopment } from "@/src/utils/environment";
import PUBLIC_PATH from "@/src/routes/public/path";

const MainPageNavigation = () => {
  const { t, i18n } = useTranslation(["mainPage", "common"]);
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Scroll effect for header background
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    {
      key: "features",
      label: t("mainPage:nav.a.features"),
      sectionId: "features",
    },
    {
      key: "contact",
      label: t("mainPage:nav.a.contact"),
      sectionId: "contact",
    },
  ];

  const handleLanguageChange = () => {
    const newLang = i18n.language === "en" ? "ko" : "en";
    i18n.changeLanguage(newLang);
  };

  const scrollToSection = (sectionId: string) => {
    // Contact navigates to contact-us page
    if (sectionId === "contact") {
      navigate(PUBLIC_PATH.CONTACT_US);
      return;
    }

    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleLogin = () => {
    navigate("/login");
  };

  const handleStartTrial = () => {
    navigate(PUBLIC_PATH.SIGNUP);
  };

  return (
    <NavContainer $isScrolled={isScrolled}>
      <NavContent>
        {/* Left: Logo (280px) */}
        <LogoSection to="/">
          <LogoImage
            src={isDevelopment ? logoExportDev : logoExport}
            alt="ECOYA EXPORT"
          />
        </LogoSection>

        {/* Center: Navigation Links (flex-grow) */}
        <NavLinks>
          {navLinks.map((link) => (
            <NavLinkButton
              key={link.key}
              onClick={() => scrollToSection(link.sectionId)}
              type="button"
            >
              <Typo typoType="b8m" color="gray2">
                {link.label}
              </Typo>
            </NavLinkButton>
          ))}
        </NavLinks>

        {/* Right: Actions (280px) */}
        <NavActions>
          <LoginButton onClick={handleLogin}>
            <Typo typoType="b8m" color="gray2">
              {t("mainPage:nav.button.login")}
            </Typo>
          </LoginButton>

          <Button
            buttonGrade="primary"
            buttonColor="blue"
            buttonSize={40}
            onClick={handleStartTrial}
          >
            {t("mainPage:nav.button.signup")}
          </Button>

          <LanguageButton onClick={handleLanguageChange}>
            <Icon iconSrc={GlobalLanguageSvg} iconSize={18} />
            <Typo typoType="b8m" color="gray2">
              {i18n.language === "ko" ? "Ko" : "En"}
            </Typo>
          </LanguageButton>

          {/* Mobile Menu Icon - Shows only on 640px and below */}
          <MenuButton type="button" onClick={() => setIsMobileMenuOpen(true)}>
            <Icon iconSrc={MenuIconSvg} iconSize={24} />
          </MenuButton>
        </NavActions>
      </NavContent>

      {/* Mobile Menu Panel */}
      <MobileMenuPanel
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onStartTrial={handleStartTrial}
        onNavigate={scrollToSection}
        onLanguageChange={handleLanguageChange}
        currentLanguage={i18n.language}
      />
    </NavContainer>
  );
};

export default MainPageNavigation;

// Styled Components matching Figma design exactly

const NavContainer = styled.nav<{ $isScrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: ${({ $isScrolled }) =>
    $isScrolled ? colorSet.white : "transparent"};
  box-shadow: ${({ $isScrolled }) =>
    $isScrolled ? "0 2px 8px rgba(0, 0, 0, 0.1)" : "none"};
  transition: all 0.3s ease;
  z-index: 1000;
`;

const NavContent = styled.div`
  max-width: 1920px;
  margin: 0 auto;
  padding: 20px 40px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  @media (max-width: 920px) {
    padding: 20px;
  }
`;

const LogoSection = styled(Link)`
  width: 280px;
  max-height: 40px;
  flex-shrink: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  text-decoration: none;
  color: inherit;

  @media (max-width: 920px) {
    width: auto; /* Flexible width on tablet/mobile */
  }
`;

const LogoImage = styled.img`
  height: 18px;
  width: auto;

  @media (max-width: 920px) {
    height: 18px;
    width: 137px;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  flex: 1;
  max-height: 40px;
  padding-left: 16px;

  @media (max-width: 920px) {
    display: none;
  }
`;

const NavLinkButton = styled.button`
  all: unset;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

const NavActions = styled.div`
  width: 280px;
  max-height: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 24px;
  flex-shrink: 0;

  @media (max-width: 920px) {
    width: auto;
    gap: 20px;
  }
`;

const LoginButton = styled.button`
  all: unset;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }

  @media (max-width: 920px) {
    display: none;
  }
`;

const LanguageButton = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }

  @media (max-width: 920px) {
    display: none;
  }
`;

const MenuButton = styled.button`
  all: unset;
  cursor: pointer;
  display: none;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }

  @media (max-width: 920px) {
    display: flex;
  }
`;
